import React, { useEffect, useState } from 'react';

import { PreQualificationStep, setPreQualificationStep } from 'prequalificationV2/actions';
import { AlreadyHaveActiveApplicationType } from 'enums/AlreadyHaveActiveApplicationType';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import useQueryParam, { useQueryParams } from 'hooks/useQueryParam';
import NotFoundContact from 'prequalificationV2/pages/NotFound/NotFoundContact';
import { setPrequalificationPortal } from 'prequalificationV2/actions/prequalificationPortal';
import { preQualificationApi } from 'prequalificationV2/api/PreQualification/PreQualificationApi';
import { AppRoutes } from 'prequalificationV2/routes/routes.enums';
import { setData } from 'prequalificationV2/actions/data';
import useAppNavigate from 'hooks/useAppNavigate';
import { ContractorNameSource } from 'enums/ApplicationsVariables';
import { getBorrowerByToken } from 'prequalificationV2/thunks';
import { setBorrower } from 'prequalificationV2/actions/borrower';
import Loader from 'components/Loader';

import AlreadyHaveActiveApplication from './steps/AlreadyHaveActiveApplication';
import BorrowerIncome from './steps/BorrowerIncome';
import BorrowerInformation from './steps/BorrowerInformation';
import BorrowerPhoneVerification from './steps/BorrowerPhoneVerification';
import CoBorrowerIncome from './steps/CoBorrowerIncome';
import CoBorrowerInformation from './steps/CoBorrowerInformation';
import CoBorrowerPhoneVerification from './steps/CoBorrowerPhoneVerification';
import DeclinePage from './steps/DeclinePage/DeclinePage';
import LoanInformation from './steps/LoanInformation';
import NoOptions from './steps/NoOptions';
import NotAbleToAccept from './steps/NotAbleToAccept';
import RedirectToProveLink from './steps/RedirectToProveLink';
import UnfreezeCredit from './steps/UnfreezeCredit';
import BorrowerIdentity from './steps/BorrowerIdentity';
import AcceptPosAutomatedOffer from './steps/AcceptOffer/AutomatedOffers/AcceptPosAutomatedOffer';
import AcceptHilAutomatedOffer from './steps/AcceptOffer/AutomatedOffers/AcceptHilAutomatedOffer';
import PropertyInformation from './steps/PropertyInformation/PropertyInformation';
import PromptToAddCoBorrower from './steps/PromptToAddCoBorrower/PromptToAddCoBorrower';
import CoBorrowerHasApplication from './steps/CoBorrowerHasApplication/CoBorrowerHasApplication';

const getPageByStep = (step: PreQualificationStep | null, vfp?: string | null) => {
  switch (step) {
    case PreQualificationStep.BorrowerIdentity:
      return <BorrowerIdentity />;
    case PreQualificationStep.LoanInformation:
      return <LoanInformation />;

    case PreQualificationStep.BorrowerPhoneVerification:
      return <BorrowerPhoneVerification />;

    case PreQualificationStep.CoBorrowerPhoneVerification:
      return <CoBorrowerPhoneVerification />;

    case PreQualificationStep.BorrowerInfo:
      return <BorrowerInformation />;

    case PreQualificationStep.CoBorrowerInfo:
      return <CoBorrowerInformation />;

    case PreQualificationStep.BorrowerIncome:
      return <BorrowerIncome />;

    case PreQualificationStep.CoBorrowerIncome:
      return <CoBorrowerIncome />;

    case PreQualificationStep.PropertyInformation:
      return <PropertyInformation />;

    case PreQualificationStep.AlreadyHaveActiveApplication:
      return <AlreadyHaveActiveApplication pageType={AlreadyHaveActiveApplicationType.Borrower} />;

    case PreQualificationStep.BorrowerAsCoBorrowerAlreadyHaveActiveApplication:
      return <AlreadyHaveActiveApplication pageType={AlreadyHaveActiveApplicationType.BorrowerAsCoBorrower} />;

    case PreQualificationStep.CoBorrowerAlreadyHaveActiveApplication:
      return <AlreadyHaveActiveApplication pageType={AlreadyHaveActiveApplicationType.CoBorrower} />;

    case PreQualificationStep.NotAbleToAccept:
      return <NotAbleToAccept />;

    case PreQualificationStep.UnfreezeCredit:
      return <UnfreezeCredit />;

    case PreQualificationStep.NoOptions:
      return <NoOptions />;

    case PreQualificationStep.Decline:
      return <DeclinePage />;

    case PreQualificationStep.RedirectToProveLink:
      return <RedirectToProveLink vfp={vfp!} />;

    case PreQualificationStep.NotFoundContact:
      return <NotFoundContact />;

    case PreQualificationStep.AcceptPosAutomatedOffer:
      return <AcceptPosAutomatedOffer isPrequal />;

    case PreQualificationStep.AcceptHilAutomatedOffer:
      return <AcceptHilAutomatedOffer isPrequal />;

    case PreQualificationStep.PromptToAddCoBorrower:
      return <PromptToAddCoBorrower />;

    case PreQualificationStep.CoBorrowerHasApplication:
      return <CoBorrowerHasApplication />;

    case PreQualificationStep.AdditionalCoBorrowerInfo:
      return <CoBorrowerInformation isAdditionalCoBorrower />;

    default:
      return <BorrowerIdentity />;
  }
};

const PreQualification = () => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();

  const currentPreQualificationStep = useAppSelector((state) => state.stages.currentPreQualificationStep);
  const borrowerState = useAppSelector((state) => state.borrower);
  const { isPrequalStepPreset } = useAppSelector((state) => state.prequalificationPortal);
  const params = useQueryParams();
  const isFinishAuth = params.has('vfp');
  const vfp = useQueryParam('vfp');
  const [isLoading, setIsLoading] = useState(true);

  if (isFinishAuth) {
    dispatch(setPreQualificationStep(PreQualificationStep.RedirectToProveLink));
  }

  const processLoggedInBorrowerApplications = async () => {
    try {
      dispatch(setPrequalificationPortal({ isBorrowerAuthorized: true }));

      const borrower = await dispatch(getBorrowerByToken()).unwrap();

      dispatch(
        setBorrower({
          isLocked: borrower?.locked,
          borrowerFraud: borrower?.variables.borrower_fraud,
        }),
      );

      const { hasBorrowerApplications, hasCoborrowerApplications } =
        await preQualificationApi.checkActiveApplications();

      if (!!borrower?.locked && !hasBorrowerApplications && !hasCoborrowerApplications) {
        navigator(AppRoutes.Prequalification);
        dispatch(setPreQualificationStep(PreQualificationStep.NotAbleToAccept));

        return;
      }

      if (hasBorrowerApplications) {
        navigator(AppRoutes.Prequalification);
        dispatch(setPreQualificationStep(PreQualificationStep.AlreadyHaveActiveApplication));

        return;
      }

      if (hasCoborrowerApplications) {
        navigator(AppRoutes.Prequalification);
        dispatch(setPreQualificationStep(PreQualificationStep.BorrowerAsCoBorrowerAlreadyHaveActiveApplication));

        return;
      }
    } catch (error) {
      dispatch(setPrequalificationPortal({ isBorrowerAuthorized: false }));

      localStorage.clear();
    }
  };

  const handleContractorInfo = async (): Promise<void> => {
    const intermediaryId = params.get('intermediary_id');

    // The promo link params are only on the url the consumer first arrives with. A visit without them is the
    // same application coming back (Prove returns with vfp only, internal navigation drops the query string),
    // so nothing is written here - the contractor stays until the application is submitted.
    if (!intermediaryId) {
      return;
    }

    // The promo link is identified by the intermediary it points at, that is what the application is
    // attributed to. The contractor name is only presentation, it must never decide whether the link counts
    const verifiedIntermediary = await preQualificationApi.getIntermediaryVerificationStatus({ id: intermediaryId });

    const isPromoLinkUsed = !!verifiedIntermediary?.isVerified;
    // The intermediary record is the only source of truth for the name, the url is never trusted for it
    const contractorName = isPromoLinkUsed ? verifiedIntermediary?.intermediaryName || '' : '';

    dispatch(setPrequalificationPortal({ isContractorNameInputDisabled: !!contractorName }));
    dispatch(
      setData(
        isPromoLinkUsed
          ? {
              contractorName: contractorName,
              contractorAutofillUsed: ContractorNameSource.PromoLink,
              promoCode: params.get('promo_code') || '',
              contractorId: intermediaryId || '',
            }
          : {
              // The link points at an intermediary we cannot verify, so it does not count. Anything left from
              // an earlier promo link visit is dropped rather than credited to this application.
              contractorName: '',
              contractorId: '',
              salesPerson: '',
              salespersonData: {},
              promoCode: '',
              contractorAutofillUsed: ContractorNameSource.Empty,
            },
      ),
    );
  };

  const initializePrequalificationFlow = async () => {
    try {
      setIsLoading(true);

      const isLoggedIn = !!localStorage.getItem('token');

      if (!isLoggedIn) {
        dispatch(setPrequalificationPortal({ isBorrowerAuthorized: false }));
      }

      if (isLoggedIn && borrowerState.borrowerId && !isPrequalStepPreset) {
        await processLoggedInBorrowerApplications();
      }

      await handleContractorInfo();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initializePrequalificationFlow();
  }, []);

  if (isLoading) {
    return <Loader />;
  }

  return getPageByStep(currentPreQualificationStep, vfp);
};

export default PreQualification;
